import { useState } from "react";
import { useDocumentTitle } from "usehooks-ts";
import {
  DataTableContextProvider,
  useDataTable,
} from "../DataTable/DataTableContext";
import { DataTable } from "../DataTable/DataTable";
import { EmptyCell } from "../DataTable/EmptyCell";
import Button from "../../../student/components/generic/button";
import { LearnerSolutionModal } from "./LearnerSolutionModal";

const LearnerProblemSupport = () => {
  useDocumentTitle("Learner Problem Support");
  return (
    <DataTableContextProvider
      source="/manager_new/parent-portal/learner_problem_support"
      defaults={{ sortBy: [{ id: "createdAt", desc: true }], pageSize: 20 }}
      sortable={["sk", "pid", "createdAt"]}
      filterable={["sk", "pid"]}
      downloadButton={false}
      columnOverrides={{
        data: ({ value }) =>
          !value ? (
            <EmptyCell />
          ) : (
            <pre className="max-h-48 min-w-48 overflow-y-auto whitespace-pre-wrap text-sm">
              {JSON.stringify(value, null, 2)}
            </pre>
          ),
        messages: ({ value }) =>
          !value || !value.length ? (
            <EmptyCell />
          ) : (
            value.map((message: string) => (
              <div key={message}>
                <p>{message}</p>
              </div>
            ))
          ),
        _id: ({ row }) => (
          <LearnerSolutionModal logId={(row.original as any)._id}>
            {(onClick) => (
              <Button
                type="link"
                onClick={onClick}
                disabled={!(row.original as any)._id}
              >
                View
              </Button>
            )}
          </LearnerSolutionModal>
        ),
      }}
    >
      <LearnerProblemSupportContent />
    </DataTableContextProvider>
  );
};

const LearnerProblemSupportContent = () => {
  const { setExternalFilterBy } = useDataTable();
  const [state, setState] = useState<{
    email?: string;
    courseId?: string;
    unitId?: string;
    subunitId?: string;
    type?: "preQuiz" | "postQuiz" | "practice" | "unitTest" | "courseTest";
  }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setExternalFilterBy([
      { id: "email", value: state.email },
      { id: "courseId", value: state.courseId },
      { id: "unitId", value: state.unitId },
      { id: "subunitId", value: state.subunitId },
      { id: "type", value: state.type },
    ]);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFieldsMap: Record<string, (keyof typeof state)[]> = {
    "": ["courseId", "unitId", "subunitId"],
    courseTest: ["unitId", "subunitId"],
    unitTest: ["subunitId"],
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldsToClear = clearFieldsMap[value] || [];
    setState((prev) => {
      const newState = { ...prev, [name]: value };
      fieldsToClear.forEach((field) => {
        newState[field] = undefined;
      });
      return newState;
    });
  };

  const fieldConfigs = [
    {
      key: "courseId",
      label: "Course ID",
      show: (type: string | undefined) => !!type,
    },
    {
      key: "unitId",
      label: "Unit ID",
      show: (type: string | undefined) =>
        type === "unitTest" ||
        type === "postQuiz" ||
        type === "preQuiz" ||
        type === "practice",
    },
    {
      key: "subunitId",
      label: "Subunit ID",
      show: (type: string | undefined) =>
        type === "postQuiz" || type === "preQuiz" || type === "practice",
    },
  ];

  return (
    <div className="p-6">
      <h1 className="mb-6 text-2xl font-bold">Learner Problem Support</h1>
      <form onSubmit={handleSubmit} className="mb-4 max-w-md space-y-4">
        <TextInput
          id="email"
          label="Email/Username"
          required
          value={state.email || ""}
          onChange={handleChange}
        />

        <div>
          <label
            htmlFor="type"
            className="block text-sm font-medium text-gray-700"
          >
            Type *
          </label>
          <select
            id="type"
            name="type"
            value={state.type || ""}
            onChange={handleSelectChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="">Select a type</option>
            <option value="preQuiz">Pre-Quiz</option>
            <option value="practice">Practice</option>
            <option value="postQuiz">Post-Quiz</option>
            <option value="unitTest">Unit Test</option>
            <option value="courseTest">Course Test</option>
          </select>
        </div>

        {fieldConfigs
          .filter((f) => f.show(state.type))
          .map((f) => (
            <TextInput
              key={f.key}
              id={f.key}
              label={f.label}
              value={state[f.key as keyof typeof state] || ""}
              onChange={handleChange}
            />
          ))}

        <button
          type="submit"
          className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Submit
        </button>
      </form>
      <DataTable />
    </div>
  );
};

const TextInput = ({
  id,
  label,
  value,
  onChange,
  required = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
}) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label}
      {required ? " *" : ""}
    </label>
    <input
      type="text"
      id={id}
      name={id}
      required={required}
      value={value}
      onChange={onChange}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
    />
  </div>
);

export default LearnerProblemSupport;
